import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import MapBackground from './MapBackground';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: var(--app-height, 100vh);
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Card = styled.div`
  background: rgba(20, 20, 20, 0.92);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px 32px;
  width: 95%;
  max-width: 480px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 10;
  animation: ${fadeIn} 0.5s ease;

  @media (max-width: 480px) {
    padding: 32px 22px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 36px;
  animation: ${fadeInUp} 0.6s ease;
`;

const Logo = styled.h1`
  color: white;
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #ffffff 0%, #4ade80 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 480px) {
    font-size: 30px;
  }
`;

const Tagline = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  margin: 0 0 8px;
`;

const WelcomeText = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  margin: 0;
`;

const UserEmail = styled.span`
  color: #4ade80;
  font-weight: 500;
`;

const SectionLabel = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 16px;
  animation: ${fadeInUp} 0.6s ease 0.15s both;
`;

const FeatureGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 28px;
`;

const FeatureCard = styled.button<{ accentFrom: string; accentTo: string; delay: number }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  padding: 22px 20px;
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.5s ease ${props => 0.2 + props.delay * 0.1}s both;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  gap: 18px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${props => props.accentFrom}, ${props => props.accentTo});
    opacity: 0.8;
    transition: opacity 0.3s ease, width 0.3s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    transform: translateX(4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);

    &::before {
      opacity: 1;
      width: 5px;
    }

    .feature-arrow {
      color: rgba(255, 255, 255, 0.5);
      transform: translateX(3px);
    }
  }

  &:active {
    transform: translateX(2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
`;

const FeatureIcon = styled.div<{ bgFrom: string; bgTo: string }>`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${props => props.bgFrom}20, ${props => props.bgTo}30);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  flex-shrink: 0;
`;

const FeatureInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FeatureName = styled.div`
  color: white;
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 3px;
`;

const FeatureDescription = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  line-height: 1.4;
`;

const FeatureArrow = styled.div`
  color: rgba(255, 255, 255, 0.2);
  font-size: 20px;
  transition: color 0.3s ease, transform 0.3s ease;
`;

const ComingSoonBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 3px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.35);
  margin-left: 8px;
  vertical-align: middle;
  background-image: linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent);
  background-size: 200% 100%;
  animation: ${shimmer} 3s ease infinite;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
  margin: 0 0 20px;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: ${fadeIn} 0.5s ease 0.6s both;
`;

const SignOutButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  cursor: pointer;
  padding: 6px 0;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const VersionText = styled.span`
  color: rgba(255, 255, 255, 0.15);
  font-size: 11px;
`;

interface WelcomePageProps {
  onMapsSelect: () => void;
  onResourcesSelect: () => void;
  onAdminPanel: () => void;
  onSignOut: () => void;
  userEmail: string;
  isAdmin: boolean;
}

const WelcomePage: React.FC<WelcomePageProps> = ({
  onMapsSelect,
  onResourcesSelect,
  onAdminPanel,
  onSignOut,
  userEmail,
  isAdmin,
}) => {
  useEffect(() => {
    const handleResize = () => {
      const appHeight = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          <Header>
            <Logo>JP - NWR</Logo>
            <Tagline>Railway Infrastructure Toolkit</Tagline>
            <WelcomeText>
              Signed in as <UserEmail>{userEmail}</UserEmail>
            </WelcomeText>
          </Header>

          <SectionLabel>Applications</SectionLabel>

          <FeatureGrid>
            <FeatureCard
              accentFrom="#4ade80"
              accentTo="#22c55e"
              delay={0}
              onClick={onMapsSelect}
            >
              <FeatureIcon bgFrom="#4ade80" bgTo="#22c55e">
                🗺️
              </FeatureIcon>
              <FeatureInfo>
                <FeatureName>Maps</FeatureName>
                <FeatureDescription>
                  Browse telecoms, signals, SDH, PDH, battery and LOC sites across the network
                </FeatureDescription>
              </FeatureInfo>
              <FeatureArrow className="feature-arrow">›</FeatureArrow>
            </FeatureCard>

            <FeatureCard
              accentFrom="#f59e0b"
              accentTo="#d97706"
              delay={1}
              onClick={onResourcesSelect}
            >
              <FeatureIcon bgFrom="#f59e0b" bgTo="#d97706">
                📚
              </FeatureIcon>
              <FeatureInfo>
                <FeatureName>Resources</FeatureName>
                <FeatureDescription>
                  Signal box directory, diagrams and reference materials
                </FeatureDescription>
              </FeatureInfo>
              <FeatureArrow className="feature-arrow">›</FeatureArrow>
            </FeatureCard>

            <FeatureCard
              accentFrom="#3b82f6"
              accentTo="#2563eb"
              delay={2}
              onClick={() => {}}
              style={{ opacity: 0.5, cursor: 'default' }}
            >
              <FeatureIcon bgFrom="#3b82f6" bgTo="#2563eb">
                📋
              </FeatureIcon>
              <FeatureInfo>
                <FeatureName>
                  Information
                  <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                </FeatureName>
                <FeatureDescription>
                  A library of useful information and documentation
                </FeatureDescription>
              </FeatureInfo>
              <FeatureArrow className="feature-arrow">›</FeatureArrow>
            </FeatureCard>

            {isAdmin && (
              <FeatureCard
                accentFrom="#ef4444"
                accentTo="#dc2626"
                delay={3}
                onClick={onAdminPanel}
              >
                <FeatureIcon bgFrom="#ef4444" bgTo="#dc2626">
                  ⚙️
                </FeatureIcon>
                <FeatureInfo>
                  <FeatureName>Admin Panel</FeatureName>
                  <FeatureDescription>
                    Manage users, sites and review change requests
                  </FeatureDescription>
                </FeatureInfo>
                <FeatureArrow className="feature-arrow">›</FeatureArrow>
              </FeatureCard>
            )}
          </FeatureGrid>

          <Divider />

          <FooterRow>
            <SignOutButton onClick={onSignOut}>Sign Out</SignOutButton>
            <VersionText>v2.0</VersionText>
          </FooterRow>
        </Card>
      </Container>
    </>
  );
};

export default WelcomePage;
